/* eslint-disable @typescript-eslint/no-magic-numbers */
import {
  CEJ_PERCENTAGE,
  CoordinateRecord,
  Coordinates,
  drawPerio,
  drawTeethNumbers,
  FONT_FAMILY,
  MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE,
  PERIO_CEJ_DEFAULT_BOTTOM_POSITION,
  PERIO_CEJ_DEFAULT_UPPER_POSITION,
  PERIO_CONTAINER_WIDTH,
  PERIO_FURCATION_COLOR,
  PERIO_JAW_SIDE_LABEL_WIDTH,
  PERIO_JAW_SPACE_BETWEEN_FACIAL_AND_LINGUAL,
  PERIO_MOBILITY_COLOR,
  PERIO_QUADRANT_PADDING_X,
  Svg,
  SvgGroup,
  SvgRect,
  SvgText,
  TEETH_NUMBER_FONT_SIZE,
  TEETH_NUMBER_FONT_WEIGHT,
  TEETH_NUMBER_TEXT_COLOR,
  Tooth,
  TOOTH_PERIO_HEIGHT,
  ToothSide,
} from '@libs/ui-lib';
import { curveBumpX, line } from 'd3-shape';
import { ScreenSize } from '@libs/shared-services';
import { PERIO_TOP_PADDING } from '../teeth/teeth.constants';
import {
  drawBackgroundLine,
  drawFurcation,
  drawGMLineByCoordinates,
  drawMobility,
  drawPerioCircles,
  drawPocketDepth,
} from '../teeth/drawing';
import { PerioJaw } from './perio-jaw.interface';

export const perioLineGenerator = line<{ x: number; y: number }>()
  .x((d) => d.x)
  .y((d) => d.y)
  .curve(curveBumpX);

export const drawBackgroundLines = (
  svg: Svg | SvgGroup,
  teethCoordinates: CoordinateRecord,
  cejPosition: 'lower' | 'upper',
  reverse?: boolean,
): void => {
  const cej_percentage = cejPosition === 'lower' ? CEJ_PERCENTAGE : 1 - CEJ_PERCENTAGE;

  // Get all tooth numbers and find first and last tooth coordinates
  const toothNumbers = Object.keys(teethCoordinates).map(Number);
  const firstToothNumber = toothNumbers[0];
  const lastToothNumber = toothNumbers.at(-1) ?? firstToothNumber;
  const firstToothCoordinate = reverse ? teethCoordinates[lastToothNumber] : teethCoordinates[firstToothNumber];
  const lastToothCoordinate = reverse ? teethCoordinates[firstToothNumber] : teethCoordinates[lastToothNumber];

  //draw the CEJ line
  drawBackgroundLine(svg, firstToothCoordinate, lastToothCoordinate, cej_percentage, '#035ADDA3');
  //draw 5 (or 12, depending on tooth side) lines below the CEJ lines, each line is 0.03649635036 percentage apart
  for (let i = 1; i < (cejPosition === 'lower' ? PERIO_CEJ_DEFAULT_BOTTOM_POSITION : PERIO_CEJ_DEFAULT_UPPER_POSITION); i++) {
    drawBackgroundLine(
      svg,
      firstToothCoordinate,
      lastToothCoordinate,
      cej_percentage + i * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE,
      '#E6EDF3',
      1,
    );
  }
  //draw 10 (or 5, depending on tooth side) lines above the CEJ lines, each line is 0.03649635036 percentage apart
  for (let i = 1; i < (cejPosition === 'lower' ? PERIO_CEJ_DEFAULT_UPPER_POSITION : PERIO_CEJ_DEFAULT_BOTTOM_POSITION); i++) {
    drawBackgroundLine(
      svg,
      firstToothCoordinate,
      lastToothCoordinate,
      cej_percentage - i * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE,
      '#E6EDF3',
      1,
    );
  }
};

export const drawDividerLine = (svg: Svg | SvgGroup, xEnd: number, height: number): void => {
  const lineCenter = xEnd; // Place line center AFTER the quadrant end

  // Draw background rectangle for the padding area
  svg
    .append('rect')
    .attr('x', lineCenter - PERIO_QUADRANT_PADDING_X)
    .attr('y', 0)
    .attr('width', PERIO_QUADRANT_PADDING_X * 2)
    .attr('height', height)
    .attr('fill', 'white');

  // Draw the divider line in the middle of the padding area
  const dividerPath = perioLineGenerator([
    { x: lineCenter, y: 0 },
    { x: lineCenter, y: height },
  ]);
  if (!dividerPath) return;
  drawPerio(svg, dividerPath, { stroke: '#E6EDF3', strokeWidth: 1, opacity: 1 });
};

export const drawSideLabels = (
  svg: Svg | SvgGroup,
  containerHeight: number,
  paddingY: number,
  teethTextColor = TEETH_NUMBER_TEXT_COLOR,
  upperText: string,
  lowerText: string,
  x: number = 10,
): SvgGroup => {
  const textYTop = TEETH_NUMBER_FONT_SIZE / 2;
  const textYBottom = containerHeight - TEETH_NUMBER_FONT_SIZE / 2 - paddingY;

  const sideLabelGroup = svg.append('g').attr('data-id', 'perio-side-label').attr('height', containerHeight);

  sideLabelGroup
    .append('text')
    .attr('x', x)
    .attr('y', textYTop)
    .attr('dominant-baseline', 'middle')
    .attr('fill', teethTextColor)
    .attr('font-family', FONT_FAMILY)
    .attr('font-size', TEETH_NUMBER_FONT_SIZE)
    .attr('font-weight', TEETH_NUMBER_FONT_WEIGHT)
    .attr('style', 'user-select: none; -webkit-user-select: none;')
    .text(upperText);

  sideLabelGroup
    .append('text')
    .attr('x', x)
    .attr('y', textYBottom)
    .attr('dominant-baseline', 'middle')
    .attr('fill', teethTextColor)
    .attr('font-family', FONT_FAMILY)
    .attr('font-size', TEETH_NUMBER_FONT_SIZE)
    .attr('font-weight', TEETH_NUMBER_FONT_WEIGHT)
    .attr('style', 'user-select: none; -webkit-user-select: none;')
    .text(lowerText);

  return sideLabelGroup;
};

export const drawRulerLabels = (
  svg: Svg | SvgGroup,
  height: number,
  numberColor: string = '#3D4A56',
  spaceBetweenFacialAndLingual = PERIO_JAW_SPACE_BETWEEN_FACIAL_AND_LINGUAL,
): (SvgRect | SvgText)[] => {
  //CEJ and numbers for buccal
  const buccalCej5Coordinates: Coordinates = {
    x: 10,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') * CEJ_PERCENTAGE -
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 5,
  };
  const buccalCej10Coordinates: Coordinates = {
    x: 10,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') * CEJ_PERCENTAGE -
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 10,
  };

  //CEJ and numbers for lingual
  const lingualCej5Coordinates: Coordinates = {
    x: 10,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      spaceBetweenFacialAndLingual +
      (1 - CEJ_PERCENTAGE) * TOOTH_PERIO_HEIGHT('xl') +
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 5,
  };
  const lingualCej10Coordinates: Coordinates = {
    x: 10,
    y:
      PERIO_TOP_PADDING +
      TOOTH_PERIO_HEIGHT('xl') +
      spaceBetweenFacialAndLingual +
      (1 - CEJ_PERCENTAGE) * TOOTH_PERIO_HEIGHT('xl') +
      TOOTH_PERIO_HEIGHT('xl') * MEASURE_BACKGROUND_LINE_DISTANCE_PERCENTAGE * 10,
  };

  const textElements: (SvgText | SvgRect)[] = [];

  textElements.push(
    //this rectangle is the background for the CEJ grid texts on the left
    svg
      .append('rect')
      .attr('original-x', 0)
      .attr('quadrant-x', PERIO_CONTAINER_WIDTH('xl') / 2 - 20)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', PERIO_JAW_SIDE_LABEL_WIDTH)
      .attr('height', height)
      .attr('fill', 'white'),
  );

  [
    {
      coordinates: buccalCej5Coordinates,
      text: '5',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: numberColor,
    },
    {
      coordinates: buccalCej10Coordinates,
      text: '10',
      rotate: false,
      fontSize: 12,
      fontWeight: 700,
      textColor: numberColor,
    },

    { coordinates: lingualCej5Coordinates, text: '5', rotate: false, fontSize: 12, fontWeight: 700, textColor: numberColor },
    { coordinates: lingualCej10Coordinates, text: '10', rotate: false, fontSize: 12, fontWeight: 700, textColor: numberColor },
  ].forEach(({ coordinates, text, rotate, fontSize, fontWeight, textColor }) => {
    textElements.push(
      svg
        .append('text')
        .attr('original-x', coordinates.x)
        .attr('quadrant-x', coordinates.x + PERIO_CONTAINER_WIDTH('xl') / 2 - 20)
        .attr('hide-in-full', 'false')
        .attr('hide-in-top-jaw', 'false')
        .attr('hide-in-lower-jaw', 'false')
        .attr('hide-in-compare', 'true')
        .attr('x', coordinates.x)
        .attr('y', coordinates.y)
        .attr('transform', rotate ? 'rotate(-90, 0, 0)' : '')
        .attr('transform-origin', `${coordinates.x}px ${coordinates.y}px`)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('fill', textColor ?? '#9EA5AB')
        .attr('font-family', FONT_FAMILY)
        .attr('font-size', fontSize ?? 20)
        .attr('font-weight', fontWeight ?? 300)
        .attr('opacity', 1)
        .attr('style', 'user-select: none; -webkit-user-select: none;')
        .text(text),
    );
  });
  return textElements;
};

export const drawPerioTreatments = (
  jawGroup: Svg | SvgGroup,
  filteredTeeth: Tooth[],
  toothSide: ToothSide,
  jawCoords: PerioJaw,
  jawHeight: number,
  screenSize: ScreenSize,
  hasPerioMeasurements: boolean,
): void => {
  // Remove existing treatmentsLayer if it exists
  jawGroup.select('[data-id="perio-treatments-layer"]').remove();

  // Create a new treatmentsLayer
  const treatmentsLayer = jawGroup.append('g').attr('data-id', 'perio-treatments-layer');

  if (hasPerioMeasurements) {
    drawGMLineByCoordinates(
      treatmentsLayer,
      jawCoords.buccalPeriosCoordinates.map((t) => ({ x: t.gingivalMargin.x, y: t.gingivalMargin.y })),
      'buccal',
      toothSide === 'buccal' ? 1 : 0.5,
    );
    drawGMLineByCoordinates(
      treatmentsLayer,
      jawCoords.lingualPeriosCoordinates.map((t) => ({ x: t.gingivalMargin.x, y: t.gingivalMargin.y })),
      'lingual',
      toothSide === 'lingual' ? 1 : 0.5,
    );
  }

  // Draw teeth numbers
  drawTeethNumbers(treatmentsLayer, jawCoords.buccalTeethCoordinates, 'upper', screenSize, jawHeight, 22);

  // Draw pocket depths
  drawPocketDepth(
    treatmentsLayer,
    filteredTeeth,
    jawCoords.buccalPeriosCoordinates,
    toothSide === 'buccal' ? 1 : 0.5,
    jawCoords.buccalCejPosition,
    'buccal',
    'perio',
    1,
  );
  drawPocketDepth(
    treatmentsLayer,
    filteredTeeth,
    jawCoords.lingualPeriosCoordinates,
    toothSide === 'lingual' ? 1 : 0.5,
    jawCoords.lingualCejPosition,
    'lingual',
    'perio',
    1,
  );

  // Draw perio circles
  drawPerioCircles(treatmentsLayer, jawCoords.buccalPeriosCoordinates, toothSide === 'buccal' ? 1 : 0.5, jawCoords.buccalCirclesPosition);
  drawPerioCircles(
    treatmentsLayer,
    jawCoords.lingualPeriosCoordinates,
    toothSide === 'lingual' ? 1 : 0.5,
    jawCoords.lingualCirclesPosition,
  );

  // Draw mobility
  drawMobility(treatmentsLayer, filteredTeeth, jawCoords.buccalTeethCoordinates, PERIO_MOBILITY_COLOR, jawCoords.mobilityPosition);

  // Draw furcation
  drawFurcation(treatmentsLayer, filteredTeeth, jawCoords.buccalTeethCoordinates, PERIO_FURCATION_COLOR, jawCoords.mobilityPosition);
};
